import { linearTiming } from "@remotion/transitions";
import {
  TransitionDirection,
  TransitionKind,
  TransitionSpeed,
} from "@repo/api/transition";
import { msToFrames } from "../../utils/timing.ts";
import {
  ImageTransitionBuilder,
  ImageTransitionBuilderProps,
} from "./ImageTransitionBuilder.ts";
import {
  splitScreenWipe,
  SplitScreenWipeDirection,
  SplitScreenWipeProps,
} from "./SplitScreenWipePresentation.tsx";

const transitionKind = TransitionKind.enum.SPLIT_SCREEN_WIPE;

// Longer than most transitions because of the hold: at NORMAL that's a 600ms
// wipe in, 800ms of split screen and a 600ms wipe out.
const SPLIT_SCREEN_WIPE_DURATION_MS: Record<TransitionSpeed, number> = {
  SLOW: 2800,
  NORMAL: 2000,
  FAST: 1400,
};

// Lead-in before and tail after the transition in the dialog preview. The
// default preview clips are shorter than this transition.
const PREVIEW_BEFORE_MS = 800;
const PREVIEW_AFTER_MS = 1000;

const directions: Partial<
  Record<TransitionDirection, SplitScreenWipeDirection>
> = {
  FROM_LEFT: "from-left",
  FROM_RIGHT: "from-right",
  FROM_TOP: "from-top",
  FROM_BOTTOM: "from-bottom",
};

function getSplitScreenWipeDurationInFrames(
  props: ImageTransitionBuilderProps,
) {
  return msToFrames(
    SPLIT_SCREEN_WIPE_DURATION_MS[props.imageTransition.speed ?? "NORMAL"],
    props.fps,
  );
}

export const ImageSplitScreenWipeTransitionBuilder: ImageTransitionBuilder<SplitScreenWipeProps> =
  {
    formConfig: {
      kind: transitionKind,
      buildDefaultValue: () => ({
        kind: transitionKind,
        speed: "NORMAL",
        direction: "FROM_RIGHT",
      }),
      speed: ["SLOW", "NORMAL", "FAST"],
      direction: ["FROM_LEFT", "FROM_RIGHT", "FROM_TOP", "FROM_BOTTOM"],
    },

    transition: {
      getDurationInFrames: getSplitScreenWipeDurationInFrames,
      buildPresentation: (props) =>
        splitScreenWipe({
          direction:
            directions[props.imageTransition.direction ?? "FROM_RIGHT"] ??
            "from-right",
        }),
      // The presentation eases each wipe itself, so the timing stays linear.
      buildTiming: (props) =>
        linearTiming({
          durationInFrames: getSplitScreenWipeDurationInFrames(props),
        }),
    },

    getPreviewClipDurationsInFrames: (props) => {
      const transitionFrames = getSplitScreenWipeDurationInFrames(props);
      return {
        previewClip1DurationInFrames:
          transitionFrames + msToFrames(PREVIEW_BEFORE_MS, props.fps),
        previewClip2DurationInFrames:
          transitionFrames + msToFrames(PREVIEW_AFTER_MS, props.fps),
      };
    },
  };
